import { Grid2 } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useDataPointSearchState } from '../context/DataPointSearchContext';
import { FilterSection } from '../DataPointSearch.styles';
import { useCascadingFilters } from '../hooks/useCascadingFilters';
import { useDeviceNameFormatter } from '../hooks/useDeviceNameFormatter';
import { DeviceTypeCategory, FilterField } from '../types';
import { formatCategoryDisplay, hasActiveFilters } from '../utils';
import ClearFiltersButton from './ClearFiltersButton';
import FilterDropdown from './FilterDropdown';

interface FilterConfig {
  field: FilterField;
  label: string;
}

const FilterCascade: React.FC = () => {
  const state = useDataPointSearchState();
  const formatDeviceName = useDeviceNameFormatter();
  const {
    filters,
    options,
    loading,
    handleFilterChange,
    clearAllFilters,
    isFieldEnabled,
  } = useCascadingFilters();

  const showParentDevice = filters.deviceType === DeviceTypeCategory.RACK;

  const getFormatOption = useCallback(
    (field: FilterField) => {
      if (field === FilterField.DEVICE_TYPE) {
        return formatCategoryDisplay;
      }
      if (
        field === FilterField.DEVICE_ID ||
        field === FilterField.PARENT_DEVICE_ID
      ) {
        return formatDeviceName;
      }
      return undefined;
    },
    [formatDeviceName],
  );

  const filterConfig: FilterConfig[] = useMemo(() => {
    const config: FilterConfig[] = [
      { field: FilterField.DEVICE_TYPE, label: 'Category' },
    ];

    if (showParentDevice) {
      config.push({
        field: FilterField.PARENT_DEVICE_ID,
        label: 'Parent Device',
      });
    }

    config.push(
      { field: FilterField.DEVICE_ID, label: 'Device ID' },
      { field: FilterField.POINT_NAME, label: 'Point Name' },
    );

    return config;
  }, [showParentDevice]);

  const hasGridFilters = state.gridFilters.length > 0;
  const isAnyFilterActive = hasActiveFilters(filters) || hasGridFilters;

  return (
    <FilterSection>
      <Grid2 container spacing={2} alignItems='center'>
        {filterConfig.map(({ field, label }) => (
          <FilterDropdown
            key={field}
            field={field}
            label={label}
            value={filters[field]}
            options={options[field] || []}
            loading={loading[field]}
            disabled={!isFieldEnabled(field)}
            onChange={(value) => handleFilterChange(field, value)}
            formatOption={getFormatOption(field)}
          />
        ))}
        <ClearFiltersButton
          onClick={clearAllFilters}
          disabled={!isAnyFilterActive}
        />
      </Grid2>
    </FilterSection>
  );
};

FilterCascade.displayName = 'FilterCascade';

export default FilterCascade;
